import { useCallback, useEffect, useRef } from 'react'
import { SystemError } from '~/lib/error/systemError'

type UseIkyuLoginParams = {
  afterLogin: () => Promise<void>
  message?: string
  signUpOnly?: boolean
}

export function useIkyuLogin<T extends HTMLElement>({
  afterLogin,
  message,
  signUpOnly = false,
}: UseIkyuLoginParams) {
  const ikyuLoginRef = useRef(null) as React.MutableRefObject<HTMLElement | null>
  const containerRef = useRef(null) as React.MutableRefObject<T | null>

  const handleAfterLogin = useCallback(async () => {
    ikyuLoginRef.current?.setAttribute('open', 'false')
    await afterLogin()
  }, [ikyuLoginRef, afterLogin])

  // ikyu-loginへのイベントハンドラの設定
  useEffect(() => {
    if (!containerRef.current) {
      throw new SystemError('containerRef is not bound.')
    }
    ikyuLoginRef.current = containerRef.current.querySelector('ikyu-login')
    const ikyuLogin = ikyuLoginRef.current
    if (!ikyuLogin) {
      throw new SystemError('no ikyu login')
    }

    if (message) {
      ikyuLogin.setAttribute('message', message)
    }
    ikyuLogin.addEventListener('close', () => {
      ikyuLogin.setAttribute('open', 'false')
      ikyuLogin.removeEventListener('login', handleAfterLogin)
    })

    ikyuLogin.addEventListener('login', handleAfterLogin)
    ikyuLogin.setAttribute('signup-only', signUpOnly.toString())

    return function () {
      ikyuLogin.removeEventListener('login', handleAfterLogin)
    }
  }, [ikyuLoginRef, handleAfterLogin, message, signUpOnly])

  const openLogin = useCallback(() => {
    const ikyuLogin = ikyuLoginRef.current
    if (!ikyuLogin) {
      throw new SystemError('no ikyu login')
    }

    ikyuLogin.setAttribute('open', 'true')
  }, [ikyuLoginRef])

  return { containerRef, openLogin }
}
